import { request } from '../utils';

/**
 * Cliente HTTP para API Web Agent do Supervisor.
 * Usa CTI_SITUACAO + autenticação JWT/sessão de operador.
 *
 * Base: https://supervisor:8080/api/?endpoint=web_agent
 */
export class LorentzClient {
  constructor({ apiBaseUrl, token, agentId, groupId, extension, groupName }) {
    this.apiBaseUrl = (apiBaseUrl || '').replace(/\/$/, '');
    this.token = token;
    this.agentId = agentId;
    this.groupId = groupId;
    this.extension = extension;
    this.groupName = groupName;
    this._ctiLoggedIn = false;
  }

  setToken(token) {
    this.token = token;
  }

  setAgentContext({ agentId, groupId, extension, groupName }) {
    if (agentId !== undefined) this.agentId = agentId;
    if (groupId !== undefined) this.groupId = groupId;
    if (extension !== undefined) this.extension = extension;
    if (groupName !== undefined) this.groupName = groupName;
  }

  _url(sub, extra = {}) {
    const q = new URLSearchParams({ endpoint: 'web_agent', sub, ...extra });
    const sep = this.apiBaseUrl.includes('?') ? '&' : '?';
    return `${this.apiBaseUrl}${sep}${q}`;
  }

  async _call(sub, { method = 'GET', body, params } = {}) {
    const endpoint = this._url(sub, params);
    const headers = this.token ? { Authorization: `Bearer ${this.token}` } : {};
    const res = await request({ endpoint, method, body, headers, retries: 3, qdelay: 10 });
    return res;
  }

  /** GET sub=me */
  async getAgentConfig() {
    const res = await this._call('me');
    return res?.data ?? res;
  }

  /** POST sub=register — cria linha CTI_SITUACAO */
  async register() {
    const res = await this._call('register', { method: 'POST', body: {} });
    return res?.data ?? res;
  }

  /** POST sub=login — pacote CTI tipo 1 */
  async login({ group, agentLogin } = {}) {
    const body = {};
    if (group) body.group = group;
    if (agentLogin) body.agent_login = agentLogin;
    if (this.groupName) body.group_name = this.groupName;
    if (this.agentId) body.agent_id = this.agentId;
    if (this.groupId) body.group_id = this.groupId;
    if (this.extension) body.extension = this.extension;

    const res = await this._call('login', { method: 'POST', body });
    const data = res?.data ?? res;
    this._ctiLoggedIn = !!data?.logged_in;
    return data;
  }

  /** POST sub=logout */
  async logout() {
    const res = await this._call('logout', { method: 'POST', body: {} });
    this._ctiLoggedIn = false;
    return res?.data ?? res;
  }

  /** GET sub=pause-reasons */
  async listPauseReasons() {
    const res = await this._call('pause-reasons');
    return res?.data?.reasons ?? res?.reasons ?? [];
  }

  /** GET sub=pause-state */
  async getPauseState() {
    const res = await this._call('pause-state');
    return res?.data ?? res;
  }

  /** GET sub=poll — RING, DIAL, estados do LorentzCTI */
  async poll({ writeState = true } = {}) {
    const res = await this._call('poll', {
      params: writeState ? { write_state: '1' } : { write_state: '0' }
    });
    return res?.data ?? res;
  }

  /** GET sub=power-dialing — campanha preditiva */
  async powerDialing() {
    const res = await this._call('power-dialing');
    return res?.data ?? res;
  }

  /** POST sub=pause — pausa eventual */
  async pauseEventual(action = 'activate') {
    return this._call('pause', {
      method: 'POST',
      body: { type: 'eventual', eventual_action: action, active: action !== 'deactivate' }
    });
  }

  /** POST sub=pause — pausa produtiva (SP_WebClientePausa) */
  async pauseProductive({ active = true, reasonId }) {
    return this._call('pause', {
      method: 'POST',
      body: { type: 'productive', reason_id: reasonId, active }
    });
  }

  /** DELETE → POST pause com active false */
  async unpause() {
    return this.pauseEventual('deactivate');
  }

  /** GET sub=contact */
  async lookupContact(phone) {
    const res = await this._call('contact', { params: { phone } });
    return res?.data ?? res;
  }

  /** GET sub=call — detalhes por CodAtendimento */
  async getCallById(callId) {
    const res = await this._call('call', { params: { call_id: String(callId) } });
    return res?.data ?? res;
  }

  /** GET sub=queue — fila de espera */
  async getQueue() {
    const res = await this._call('queue');
    return res?.data ?? res;
  }

  /** GET sub=qualification */
  async getQualificationForm(callId, phone) {
    const res = await this._call('qualification', {
      params: { call_id: String(callId || ''), phone: phone || '' }
    });
    return res?.data ?? res;
  }

  async listQualifications() {
    const form = await this.getQualificationForm(0, '');
    return form?.qualifications ?? [];
  }

  /** POST sub=qualification */
  async submitQualification(callId, { qualificationId, phone, companions }) {
    const res = await this._call('qualification', {
      method: 'POST',
      body: {
        call_id: callId,
        qualification_id: qualificationId,
        phone,
        companions
      }
    });
    return res?.data ?? res;
  }

  /** POST sub=dial */
  async dial(phone) {
    return this._call('dial', { method: 'POST', body: { phone } });
  }

  /** POST sub=answer — gancho CTI */
  async answer() {
    return this._call('answer', { method: 'POST', body: {} });
  }

  /** POST sub=hangup */
  async hangup() {
    return this._call('hangup', { method: 'POST', body: {} });
  }

  /** POST sub=transfer */
  async transfer(destination) {
    return this._call('transfer', { method: 'POST', body: { destination } });
  }

  /** POST sub=capture */
  async capture() {
    return this._call('capture', { method: 'POST', body: {} });
  }

  /** POST sub=park */
  async park({ retrieve = false, slot } = {}) {
    return this._call('park', {
      method: 'POST',
      body: { retrieve, slot }
    });
  }

  /** GET/POST sub=recording */
  async getRecordingState() {
    const res = await this._call('recording');
    return res?.data ?? res;
  }

  async startRecording() {
    return this._call('recording', { method: 'POST', body: { action: 'start' } });
  }

  async endRecording({ callId, startedAt } = {}) {
    return this._call('recording', {
      method: 'POST',
      body: { action: 'end', call_id: callId, started_at: startedAt }
    });
  }

  /** Compat: contexto de chamada via poll */
  async getCallContext({ callId, phone }) {
    const poll = await this.poll();
    return {
      call_id: poll?.call_id || callId,
      phone: poll?.phone || phone,
      command: poll?.command,
      extension_state: poll?.extension_state,
      legacy: poll?.legacy,
      contact: poll?.contact
    };
  }
}

export default LorentzClient;
